import { computed } from 'vue';
import type { ComputedRef } from 'vue';
import { menuV2EnabledFor } from '@/composition/use-menu-v2';
import { menuV2NewUntil } from '@/domain/catalog/menu-v2-rollout';
import { buildRestaurantNav, filterNavForRole } from '@/domain/layout/app-nav';
import type { INavGroup, INavRole } from '@/domain/layout/app-nav';
import type { IRestaurant } from '@/interfaces/IRestaurant';
import { getEffectiveTier } from '@/utils/billing-entitlements.utils';
import { vueData } from '@/VueData';

/** Whose navigation: the store and the member browsing it. */
export interface IRestaurantNavMember {
  restaurant: IRestaurant;
  role: INavRole;
  isStaff: boolean;
}

/**
 * The store's drawer navigation as this member sees it (plan §4.2; role
 * filter applied). Shared by the restaurant drawer and the assistant's page
 * targets (plan §9.7), so both always offer the same pages.
 */
export function restaurantNavFor(member: IRestaurantNavMember): INavGroup[] {
  const { restaurant, role, isStaff } = member;
  return filterNavForRole(
    buildRestaurantNav({
      stringId: restaurant.stringId,
      country: restaurant.country,
      restaurantId: restaurant.id,
      effectiveTier: getEffectiveTier(restaurant),
      role,
      isStaff,
      clubLaunchChip: vueData.clubLaunchChip,
      menuV2: menuV2EnabledFor(restaurant.stringId),
      menuV2NewUntil: menuV2NewUntil(),
      now: new Date(),
    }),
    role
  );
}

/** `restaurantNavFor` as a computed; no member (outside a store) → no entries. */
export function useRestaurantNav(
  member: () => IRestaurantNavMember | null
): ComputedRef<INavGroup[]> {
  return computed(() => {
    const current = member();
    return current ? restaurantNavFor(current) : [];
  });
}
